import React, { useEffect, useRef } from 'react'; // Import the custom tooltip
import { Link } from 'react-router-dom';
import { EXTRA_FEATURES, generateTooltipText } from './config';
import { useSelector } from 'react-redux';
import { uiState, userState } from '../../store';
import CustomSideNavTooltip from './CustomTooltip';

interface SidebarItemProps {
  featureKey: keyof ReturnType<typeof generateTooltipText>;
  feature?: string;
  route: string;
  isActive: boolean;
  icon: React.ReactNode;
  children?: React.ReactNode;
  expandedMenuKey?: string | null;
  setExpandedMenuKey?: React.Dispatch<React.SetStateAction<string | null>>;
  menuKey?: string;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  featureKey,
  feature,
  route,
  isActive,
  icon,
  children,
  expandedMenuKey,
  setExpandedMenuKey,
  menuKey,
}) => {
  const userData = useSelector(userState);
  const { collapsed } = useSelector(uiState);
  const isExpanded = expandedMenuKey === featureKey;
  const [shouldAnimate, setShouldAnimate] = React.useState(false);
  const submenuRef = useRef<HTMLUListElement>(null);

  const toggleExpand = () => {
    if (setExpandedMenuKey) {
      setExpandedMenuKey(isExpanded ? null : featureKey);
      setShouldAnimate(true);
    }
  };

  const hasChildren = !!children;

  const handleClick = (e: React.MouseEvent) => {
    if (!isFeatureEnabled) {
      e.preventDefault();
    } else if (hasChildren) {
      e.preventDefault();
      toggleExpand();
    }
  };

  const user = userData.userData;

  const enabledFeatures = user?.plan.features;

  const shouldRenderDiv =
    EXTRA_FEATURES.includes(feature as string) &&
    !(enabledFeatures as readonly string[])?.includes(feature as string);

  const isAdmin = user?.RoleId === 1;

  const isFeatureEnabled = isAdmin || enabledFeatures?.includes(feature);

  const tooltipText = generateTooltipText();

  const tooltipContent = isFeatureEnabled
    ? tooltipText[featureKey].default
    : tooltipText[featureKey].disabled;

  const linkStyle = isFeatureEnabled
    ? {}
    : {
        color: '#bfb6b6',
        cursor: 'not-allowed',
        textDecoration: 'none',
      };

  const tooltipMap = generateTooltipText();

  const fullTooltipContent =
    hasChildren && collapsed ? (
      <div>
        <div>{tooltipContent}</div>
        <hr />
        <div style={{ padding: '6px 0px' }}>
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child) && child.props.featureKey) {
              const key = child.props.featureKey as keyof typeof tooltipMap;
              const isChildActive = child.props.isActive;

              return (
                <Link
                  to={child.props.route}
                  style={{
                    display: 'block',
                    textDecoration: 'none',
                    color: '#fff',
                    fontSize: '11px',
                    padding: '4px 8px',
                    fontWeight: 400,
                    backgroundColor: isChildActive ? '#006ac3' : '#0078db',
                    borderRadius: isChildActive ? '4px' : '0px',
                  }}
                  onClick={(e) => {
                    if (!isFeatureEnabled) e.preventDefault();
                  }}
                >
                  {tooltipMap[key].default}
                </Link>
              );
            }
            return null;
          })}
        </div>
      </div>
    ) : (
      tooltipContent
    );

  // The list item content
  const listItemContent = (
    <li className={hasChildren ? 'has-children' : ''}>
      {hasChildren ? (
        <div
          onClick={handleClick}
          style={linkStyle}
          className={`menu-item-nohover ${isActive ? 'activeTab' : ''}`}
        >
          <div className="icon">{icon}</div>
          <div className="menu-name">{tooltipContent}</div>
          {!collapsed && (
            <span className={`submenu-caret ${isExpanded ? 'open' : ''}`}>
              ▶
            </span>
          )}
        </div>
      ) : (
        <Link
          to={route}
          style={linkStyle}
          onClick={handleClick}
          className={`menu-item-content ${isActive ? 'activeTab' : ''}`}
        >
          <div className="icon">{icon}</div>
          <div className="menu-name">{tooltipContent}</div>
        </Link>
      )}
      {hasChildren && isExpanded && !collapsed && (
        <ul
          ref={submenuRef}
          className={`submenu ${
            isExpanded && !collapsed ? 'submenu-expanded' : ''
          }`}
          style={{
            overflow: 'hidden',
            transition: 'height 0.3s ease, opacity 0.3s ease',
            height:
              isExpanded && !collapsed
                ? `${submenuRef.current?.scrollHeight}px`
                : '0px',
            opacity: isExpanded && !collapsed ? 1 : 0,
          }}
        >
          {children}
        </ul>
      )}
    </li>
  );

  useEffect(() => {
    const submenu = submenuRef.current;
    if (!submenu || !shouldAnimate) return;

    if (isExpanded && !collapsed) {
      submenu.style.height = '0px'; // reset first
      submenu.style.transition = 'none';

      requestAnimationFrame(() => {
        submenu.style.transition = 'height 0.3s ease';
        submenu.style.height = `${submenu.scrollHeight}px`;
      });
    } else {
      submenu.style.height = `${submenu.scrollHeight}px`; // start from full height
      submenu.style.transition = 'height 0.3s ease';

      requestAnimationFrame(() => {
        submenu.style.height = '0px';
      });
    }
  }, [isExpanded, collapsed, children, shouldAnimate]);

  useEffect(() => {
    if (!shouldAnimate) return;
    const timeout = setTimeout(() => setShouldAnimate(false), 300);
    return () => clearTimeout(timeout);
  }, [shouldAnimate]);

  return shouldRenderDiv || !isFeatureEnabled ? (
    <></>
  ) : (
    <div className={!isFeatureEnabled && !isAdmin ? 'leftNavDeactive' : ''}>
      {collapsed ? (
        <CustomSideNavTooltip
          title={fullTooltipContent}
          placement="right"
          arrow
        >
          {listItemContent}
        </CustomSideNavTooltip>
      ) : (
        listItemContent
      )}
    </div>
  );
};

export default SidebarItem;
